import type { ReactNode } from 'react'

// Panel — ATC-109 / `ATC-ADR-018`.
//
// A bordered, SHADOW-FREE white section with a micro-label header. Ported from
// Flight Deck's `PanelShell` (`detailPrimitives.tsx`), minus Tailwind.
//
// THIS PRIMITIVE IS LOAD-BEARING FOR CONFORMANCE, NOT COSMETIC. The status
// hexes clear the 3:1 graphical floor on white (success 3.30, warning 3.19) and
// MISS it on the warm canvas (2.98, 2.88). ATC's two densest tables sit bare on
// the page background today, so a status dot on one of them would be
// non-conformant. Panel is what puts them on white — which is why the palette
// flip (`ATC-110`) cannot ship before this primitive exists.
//
// Separation is a 1px stroke and a fill step. No shadow, no radius: a shadow on
// an operator surface reads as a marketing card, and token-era surfaces carry
// no corner radius.

export interface PanelProps {
  /** The micro-label header. Omit for a bare bordered container. */
  title?: ReactNode
  /** Optional right-aligned control in the header — a refresh button, a link. */
  action?: ReactNode
  /**
   * Drop the body padding, for a panel whose body is a `DataTable`. The table
   * draws its own row rules, so padding would inset it away from the panel edge
   * and the rules would stop short of the stroke.
   */
  flush?: boolean
  children: ReactNode
}

export function Panel({ title, action, flush = false, children }: PanelProps) {
  return (
    <section className={flush ? 'rx-panel rx-panel-flush' : 'rx-panel'}>
      {(title !== undefined || action !== undefined) && (
        <header className="rx-panel-head">
          {/* Rendered even when empty so `action` stays right-aligned. */}
          <h2 className="rx-panel-title">{title}</h2>
          {action}
        </header>
      )}
      <div className="rx-panel-body">{children}</div>
    </section>
  )
}
